import { Stack, Textarea, TextInput } from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import type { LangageIso6391 } from "@tfg/db"
import { Button, ErrorBox, Select } from "@tfg/ui"
import { AxiosError } from "axios"
import { useTranslation } from "react-i18next"

import { FAQ_CATEGORIES } from "~/pages/Brand/FAQ/constants"
import { LANGUAGES } from "~/utils/languages.tsx"

type Props<T> = {
  form: UseFormReturnType<T>
  onSubmit: (data: T) => void
  isLoading: boolean
  error?: AxiosError | null
}

const CATEGORY_OPTIONS = Object.keys(FAQ_CATEGORIES).map((category) => ({
  label: FAQ_CATEGORIES[category as keyof typeof FAQ_CATEGORIES],
  value: category,
}))

const LANGUAGE_OPTIONS = Object.keys(LANGUAGES).map((lang) => ({
  label: LANGUAGES[lang as LangageIso6391].languageName,
  value: lang,
}))

export const FAQForm = <T,>({ form, onSubmit, isLoading, error }: Props<T>) => {
  const { t } = useTranslation("admin")

  return (
    <form onSubmit={form.onSubmit((data) => onSubmit(data))}>
      <Stack>
        {error && <ErrorBox error={error} />}
        <Select
          label={t("faq.modal.labels.category")}
          placeholder={t("faq.modal.placeholders.category")}
          data={CATEGORY_OPTIONS}
          {...form.getInputProps("category")}
        />
        <Select
          label={t("faq.modal.labels.lang")}
          placeholder={t("faq.modal.placeholders.lang")}
          data={LANGUAGE_OPTIONS}
          {...form.getInputProps("lang")}
        />
        <TextInput
          label={t("faq.modal.labels.question")}
          placeholder={t("faq.modal.placeholders.question")}
          {...form.getInputProps("question")}
        />
        <Textarea
          autosize
          minRows={5}
          maxRows={15}
          label={t("faq.modal.labels.answer")}
          placeholder={t("faq.modal.placeholders.answer")}
          {...form.getInputProps("answer")}
        />
        <Button type="submit" loading={isLoading} disabled={!form.isValid()}>
          {t("faq.modal.submit")}
        </Button>
      </Stack>
    </form>
  )
}
